import { ChangeEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import * as stockRequestService from "../../Services/Http/StockRequestService";
import { OrderList } from "../../Models/OrderList";
import { OrderBase } from "../../Models/OrderBase";
import { Order } from "../../Models/Order";
import { toast } from "react-toastify";
import { useOrder } from "../../Context/useOrder";
import { useNavigate } from "react-router";
import Moment from "react-moment";
import { useKeyDown } from "../../Hooks/useKeyDown";
import dataStore from "../../Store/dataStore";
import { SavedAttributeName, SavedAttributes } from "../../Models/SavedAttributes";
import { ValidCountLocation } from "../../Models/ValidCountLocation";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import { Button, Modal } from "react-bootstrap";

interface Props {}

const successSound = new Audio();
successSound.src = "/assets/sounds/success.mp3";
successSound.load();

const errorSound = new Audio();
errorSound.src = "/assets/sounds/error.mp3";
errorSound.load();

//dates from the API arrive as strings - the pick screens expect Date objects
const toOrderDates = (order: Order): Order => {
  order.transDueDate = order.transDueDate ? new Date(order.transDueDate) : new Date();
  order.transDate = order.transDate ? new Date(order.transDate) : new Date();
  order.dueDate = order.dueDate ? new Date(order.dueDate) : new Date();
  return order;
};

const StockRequestListPage = observer((props: Props) => {
  const { setCurrentOrder } = useOrder();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRequests, setFilteredRequests] = useState<OrderBase[]>([]);
  const requests = useRef<OrderBase[]>([]);
  const searchBoxInputRef = useRef<HTMLInputElement>(null);
  const filterDaysInputRef = useRef<HTMLInputElement>(null);
  const filterResultLimitInputRef = useRef<HTMLInputElement>(null);
  const [showOnlyUnlocked, setShowOnlyUnlocked] = useState<boolean>(true);
  const [pickStatus, setPickStatus] = useState<string>("");
  const [filterDays, setFilterDays] = useState<number>(0);
  const [resultLimit, setResultLimit] = useState<number>(0);
  //From / To location filters ("" = all), remembered on the device
  const [filterFrom, setFilterFrom] = useState<string>(() => dataStore.attributes.attrs[SavedAttributeName.StockRequestFilterFrom] || "");
  const [filterTo, setFilterTo] = useState<string>(() => dataStore.attributes.attrs[SavedAttributeName.StockRequestFilterTo] || "");

  const [newModalOpen, setNewModalOpen] = useState<boolean>(false);
  const [locations, setLocations] = useState<ValidCountLocation[]>([]);
  const [fromLocationNo, setFromLocationNo] = useState<string>("");
  const [toLocationNo, setToLocationNo] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  useLayoutEffect(() => {
    searchBoxInputRef.current?.focus();
  }, []);

  useEffect(() => {
    setCurrentOrder(null);
    bindGrid();
    stockRequestService
      .getStockRequestLocationsAPI(dataStore.serverUrl || "", dataStore.dbKey || "")
      .then((locs) => setLocations(locs || []))
      .catch(() => setLocations([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCurrentOrder]);

  useKeyDown((event: any) => {
    onKeyPressed(event);
  }, []);

  const bindGrid = async (aShowOnlyUnlocked?: boolean, aPickStatus?: string, aFilterDays?: number, aResultLimit?: number) => {
    try {
      const resp: OrderList = await stockRequestService.getStockRequestsAPI(
        dataStore.serverUrl || "",
        dataStore.dbKey || "",
        aShowOnlyUnlocked ?? showOnlyUnlocked,
        aPickStatus ?? pickStatus,
        aFilterDays ?? filterDays,
        aResultLimit ?? resultLimit,
      );
      requests.current = resp && resp.result ? [...resp.result] : [];
    } catch (err) {
      requests.current = [];
      toast.error("Error loading stock requests");
    }
    applyFilters();
  };

  //what the list shows: the loaded requests narrowed by the search box and the From / To location filters
  const applyFilters = (term: string = searchTerm, from: string = filterFrom, to: string = filterTo) => {
    const t = term.trim().toLowerCase();
    setFilteredRequests(
      requests.current.filter(
        (r) =>
          (!t || r.transRefCode!.toLowerCase().startsWith(t)) &&
          (!from || String(r.locationNo ?? "") === from) &&
          (!to || String(r.originLocationNo ?? "") === to),
      ),
    );
  };

  const onLocationFilterChanged = (which: "from" | "to", value: string) => {
    const from = which === "from" ? value : filterFrom;
    const to = which === "to" ? value : filterTo;
    setFilterFrom(from);
    setFilterTo(to);
    runInAction(() => {
      const savedAttrs: SavedAttributes = { ...dataStore.attributes };
      savedAttrs.attrs[SavedAttributeName.StockRequestFilterFrom] = from;
      savedAttrs.attrs[SavedAttributeName.StockRequestFilterTo] = to;
      dataStore.attributes = savedAttrs;
    });
    applyFilters(searchTerm, from, to);
  };

  const openRequest = (transRefCode: string) => {
    stockRequestService
      .getStockRequestAPI(transRefCode, dataStore.serverUrl || "", dataStore.dbKey || "")
      .then((order: Order) => {
        if (order) {
          setCurrentOrder(toOrderDates(order));
          navigate("/stockrequest");
        } else {
          errorSound.play();
          toast.error("Stock request not found", { autoClose: 1000 });
        }
      })
      .catch((error: any) => {
        toast.error(error.message || error, { autoClose: false });
      });
  };

  const onSearchChanged = (passedSearchTerm?: string) => {
    applyFilters(passedSearchTerm !== undefined ? passedSearchTerm : searchTerm);
  };

  const onRequestScanned = (term?: string) => {
    let barcode = term || searchTerm;
    if (!barcode) {
      return;
    }
    barcode = barcode.toLowerCase().trim();
    const found = requests.current.find((r) => r.transRefCode!.toLowerCase() === barcode);
    successSound.play();
    //not in the list (perhaps filtered out) - ask eSuite for it anyway
    openRequest(found ? found.transRefCode! : barcode);
    setSearchTerm("");
    onSearchChanged("");
  };

  const onKeyPressed = (event: any) => {
    if (
      newModalOpen ||
      (event.target &&
        event.target instanceof HTMLInputElement &&
        [searchBoxInputRef.current?.name, filterDaysInputRef.current?.name, filterResultLimitInputRef.current?.name].includes(event.target.name))
    ) {
      return;
    }
    const keyPressed = event.key ? event.key : "";
    if (keyPressed === "") {
      return;
    }
    if (keyPressed.toLowerCase() === "tab" || keyPressed.toLowerCase() === "enter") {
      event.preventDefault();
      setSearchTerm((oldState) => {
        onRequestScanned(oldState);
        return oldState;
      });
    } else if (keyPressed.length === 1) {
      setSearchTerm((oldState) => {
        const newState = oldState + keyPressed;
        onSearchChanged(newState);
        return newState;
      });
    }
  };

  const handleKeyUp = (event: any) => {
    if (event.key && event.key.toLowerCase() === "enter") {
      event.preventDefault();
      onRequestScanned();
    } else {
      onSearchChanged();
    }
  };

  const onClearSearch = () => {
    setSearchTerm("");
    onSearchChanged("");
  };

  const handleFilterLockedChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setShowOnlyUnlocked(event.target.checked);
    bindGrid(event.target.checked);
  };
  const handlePickStatusChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    setPickStatus(event.target.value);
    bindGrid(undefined, event.target.value);
  };
  const handleFilterDaysChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const days = parseInt(event.target.value, 10) || 0;
    setFilterDays(days);
    bindGrid(undefined, undefined, days);
  };
  const handleResultLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const limit = parseInt(event.target.value, 10) || 0;
    setResultLimit(limit);
    bindGrid(undefined, undefined, undefined, limit);
  };

  //------------- New Request popup -------------
  const onShowNewRequest = () => {
    //start from the From/To used last time
    setFromLocationNo(dataStore.attributes.attrs[SavedAttributeName.StockRequestFrom] || "");
    setToLocationNo(dataStore.attributes.attrs[SavedAttributeName.StockRequestTo] || "");
    setDescription("");
    setNewModalOpen(true);
  };

  const onCloseNewRequest = () => {
    setNewModalOpen(false);
    searchBoxInputRef.current?.focus();
  };

  const onSaveNewRequest = () => {
    const from = parseInt(fromLocationNo, 10);
    const to = parseInt(toLocationNo, 10);
    if (!from || !to) {
      toast.error("Choose where the stock comes from and goes to", { autoClose: 2000 });
      return;
    }
    if (from === to) {
      toast.error("From and To must be different locations", { autoClose: 2000 });
      return;
    }
    runInAction(() => {
      const savedAttrs: SavedAttributes = { ...dataStore.attributes };
      savedAttrs.attrs[SavedAttributeName.StockRequestFrom] = fromLocationNo;
      savedAttrs.attrs[SavedAttributeName.StockRequestTo] = toLocationNo;
      dataStore.attributes = savedAttrs;
    });
    //nothing goes to eSuite yet - the request is built up on the next screen and posted in one go on Save
    const now = new Date();
    const draft: Order = {
      isDraft: true,
      locationNo: from,
      originLocationNo: to,
      fromLocationCode: locations.find((l) => l.locationNo === from)?.locationCode,
      toLocationCode: locations.find((l) => l.locationNo === to)?.locationCode,
      transComment: description.trim(),
      transDate: now,
      transDueDate: now,
      dueDate: now,
      cartLines: [],
    };
    setNewModalOpen(false);
    setCurrentOrder(draft);
    navigate("/stockrequest");
  };

  return (
    <>
      <div className="container-limited-width">
        <div className="row">
          <div className="col-12 p-2">
            <div className="input-group">
              <input
                name="searchBox"
                type="text"
                className="form-control search-box"
                placeholder="Scan request barcode or select a request"
                autoCapitalize="none"
                autoComplete="off"
                onKeyUp={handleKeyUp}
                onChange={(e) => setSearchTerm(e.target.value)}
                value={searchTerm}
                ref={searchBoxInputRef}
              />
              <div className="input-group-text search-btn" title="Search" onClick={() => onRequestScanned()}>
                <i className="fa fa-search"></i>
              </div>
              <div className="input-group-text search-btn" title="Clear" onClick={onClearSearch}>
                <i className="fa fa-times-circle-o"></i>
              </div>
            </div>
          </div>
        </div>

        <div className="row my-2">
          <div className="col-12 d-flex justify-content-start">
            <div className="form-check form-switch form-check-reverse">
              <input className="form-check-input" type="checkbox" role="switch" id="stockRequestLockSwitch" checked={showOnlyUnlocked} onChange={handleFilterLockedChange} />
              <label className="form-check-label" htmlFor="stockRequestLockSwitch">
                Lock Status
              </label>
            </div>
            <div className="ms-2">{showOnlyUnlocked ? "Unlocked" : "All"}</div>
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-12">
            <label className="form-check-label">Pick Status</label>
            <select className="form-select" onChange={handlePickStatusChange} value={pickStatus}>
              <option value="">All</option>
              <option value="UNPICKED">Unpicked</option>
              <option value="PICKED">Part Picked</option>
              <option value="ALLUNPICKED">All Unpicked</option>
              <option value="ALLPICKED">All Picked</option>
            </select>
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-6">
            <label className="form-check-label">From</label>
            <select className="form-select" value={filterFrom} onChange={(e) => onLocationFilterChanged("from", e.target.value)}>
              <option value="">All</option>
              {locations.map((l) => (
                <option key={l.locationNo} value={l.locationNo.toString()}>
                  {l.locationCode}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6">
            <label className="form-check-label">To</label>
            <select className="form-select" value={filterTo} onChange={(e) => onLocationFilterChanged("to", e.target.value)}>
              <option value="">All</option>
              {locations.map((l) => (
                <option key={l.locationNo} value={l.locationNo.toString()}>
                  {l.locationCode}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-6">
            <label className="form-check-label">Filter Days</label>
            <input
              name="filterDays"
              ref={filterDaysInputRef}
              type="number"
              min="0"
              className="form-control search-box"
              placeholder="Number of days"
              value={filterDays}
              onChange={handleFilterDaysChange}
            />
          </div>
          <div className="col-6">
            <label className="form-check-label">Result limit</label>
            <input
              name="filterResultLimit"
              ref={filterResultLimitInputRef}
              type="number"
              min="0"
              className="form-control search-box"
              value={resultLimit}
              onChange={handleResultLimitChange}
            />
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-12">
            <Button variant="primary" className="w-100" onClick={onShowNewRequest}>
              <i className="fa fa-plus me-2" aria-hidden="true"></i>NEW REQUEST
            </Button>
          </div>
        </div>

        <div className="row">
          <div className="col-12 mb-2">
            <ul className="list-group orders-list">
              {filteredRequests.map((request, index) => {
                return (
                  <li
                    key={`${request.transRefCode}_${index}`}
                    className="list-group-item d-flex justify-content-between align-items-center"
                    onClick={() => openRequest(request.transRefCode!)}
                  >
                    <div className="d-flex flex-column">
                      <div>
                        {request.transRefCode}
                        <span className="text-muted small ms-2">
                          (<Moment date={request.dueDate} format="ddd DD/MM" />)
                        </span>
                      </div>
                      {request.transComment && <div className="text-muted small">{request.transComment}</div>}
                    </div>
                    <div className="d-flex align-items-center">
                      {request.isLocked && (
                        <span className="icon-wrap">
                          <i className="fa fa-lock" aria-hidden="true"></i>
                        </span>
                      )}
                      <span className="fw-bold text-nowrap">
                        {request.fromLocationCode} <i className="fa fa-long-arrow-right mx-1" aria-hidden="true"></i> {request.toLocationCode}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      <Modal show={newModalOpen} onHide={onCloseNewRequest} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>New Stock Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            <label className="form-check-label">From</label>
            <select className="form-select" value={fromLocationNo} onChange={(e) => setFromLocationNo(e.target.value)}>
              <option value="">Choose...</option>
              {locations.map((l) => (
                <option key={l.locationNo} value={l.locationNo.toString()}>
                  {l.locationCode}
                  {l.locationDescription ? ` - ${l.locationDescription}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-2">
            <label className="form-check-label">To</label>
            <select className="form-select" value={toLocationNo} onChange={(e) => setToLocationNo(e.target.value)}>
              <option value="">Choose...</option>
              {locations.map((l) => (
                <option key={l.locationNo} value={l.locationNo.toString()}>
                  {l.locationCode}
                  {l.locationDescription ? ` - ${l.locationDescription}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-check-label">Description</label>
            <input type="text" className="form-control" maxLength={200} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={onSaveNewRequest}>
            Start
          </Button>
          <Button variant="secondary" onClick={onCloseNewRequest}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

export default StockRequestListPage;
